(function () {
  'use strict';

  function initSection(section) {
    if (!section || section.dataset.odditMainProductInit === 'true') return;
    section.dataset.odditMainProductInit = 'true';

    /* ---- Mobile gallery carousel ------------------------------------------
       On mobile the main image is a swipeable slider; the thumbnail row is its
       navigation. Clicking a thumbnail scrolls to that slide, and swiping the
       slider syncs the active thumbnail back. (On desktop the slider only shows
       the first slide and the thumbnails are hidden, so this is a no-op there.) */
    var slider = section.querySelector('[data-product-slider]');
    var slides = slider
      ? Array.prototype.slice.call(
          slider.querySelectorAll('.oddit-section-main-product__slide')
        )
      : [];
    var thumbs = Array.prototype.slice.call(
      section.querySelectorAll('.oddit-section-main-product__thumbnail')
    );

    // Scroll the (horizontally scrolling) thumbnail row so the active thumb is
    // fully visible — only touches the row's own scroll, never the page.
    function scrollThumbIntoView(index) {
      var thumb = thumbs[index];
      if (!thumb) return;
      var container = thumb.parentNode;
      if (!container || container.scrollWidth <= container.clientWidth) return;
      var tRect = thumb.getBoundingClientRect();
      var cRect = container.getBoundingClientRect();
      var delta = 0;
      if (tRect.right > cRect.right) {
        delta = tRect.right - cRect.right;
      } else if (tRect.left < cRect.left) {
        delta = tRect.left - cRect.left;
      }
      if (delta !== 0) {
        container.scrollBy({ left: delta, behavior: 'smooth' });
      }
    }

    function setActiveThumb(index) {
      thumbs.forEach(function (t, i) {
        t.classList.toggle(
          'oddit-section-main-product__thumbnail--active',
          i === index
        );
      });
      scrollThumbIntoView(index);
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var index = Number(thumb.getAttribute('data-thumbnail-index'));
        var slide = slides[index];
        if (slide && slider) {
          // Land on the slide's snap position (its inset), matching the slider's
          // scroll-padding so it doesn't scroll flush-left then snap back.
          var inset = parseFloat(getComputedStyle(slider).scrollPaddingLeft) || 0;
          var delta =
            slide.getBoundingClientRect().left -
            slider.getBoundingClientRect().left -
            inset;
          slider.scrollBy({ left: delta, behavior: 'smooth' });
        }
        setActiveThumb(index);
      });
    });

    // Sync the active thumbnail as the shopper swipes the slider.
    if (slider && slides.length && 'IntersectionObserver' in window) {
      var slideObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              var idx = slides.indexOf(entry.target);
              if (idx !== -1) setActiveThumb(idx);
            }
          });
        },
        { root: slider, threshold: [0.6] }
      );
      slides.forEach(function (slide) {
        slideObserver.observe(slide);
      });
    }

    /* ---- Sticky add-to-cart bar -------------------------------------------
       Pin the mobile buy bar once the in-page buy buttons (the anchor) scroll
       out of view, and hide it again when they return. Mirrors the legacy
       sticky-bottom behavior, observer-based. The body class reserves footer
       space (see the CSS) only while the bar is showing. */
    var stickyBar = section.querySelector('[data-sticky-atc]');
    if (stickyBar && 'IntersectionObserver' in window) {
      var anchorSel = stickyBar.getAttribute('data-sticky-atc-anchor');
      var anchor = anchorSel ? section.querySelector(anchorSel) : null;
      if (anchor) {
        var stickyObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            // Show only when the anchor has scrolled above the viewport, not
            // when it's still below the fold on load.
            var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            stickyBar.classList.toggle(
              'oddit-section-main-product__sticky-atc--visible',
              show
            );
            stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
            document.body.classList.toggle('oddit-sticky-atc-active', show);
          });
        });
        stickyObserver.observe(anchor);
      }
    }

    /* ---- Inline gallery video autoplay (RC-61) -----------------------------
       The team wants a gallery video to play on load rather than waiting for a
       tap. The Liquid renders video as a poster image in the main slider and as
       a click-to-open tile in the grid, with the only real <video> living in the
       lightbox. Rather than duplicate the source plumbing into the template, we
       borrow the sources from that lightbox video and build an inline, muted,
       looping copy in place.

       `muted` MUST be set as a property (not just an attribute) before play() —
       Safari ignores a late attribute and rejects the promise. `playsinline`
       stops iOS going fullscreen. Anyone with prefers-reduced-motion keeps the
       static poster and the existing click-to-open behaviour.

       Runs BEFORE the zoom wiring below on purpose: a video slide has its
       [data-zoom-image] hook removed, so the hover magnifier never tries to feed
       a video URL into the zoom lightbox's <img>. */
    var inlineVideos = [];

    function buildInlineVideo(sourceEl, posterUrl, className) {
      var video = document.createElement('video');
      video.className = className;
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.tabIndex = -1;
      video.setAttribute('muted', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('aria-hidden', 'true');
      video.setAttribute('data-gallery-video', '');
      if (posterUrl) video.poster = posterUrl;
      var source = document.createElement('source');
      source.src = sourceEl.getAttribute('src');
      if (sourceEl.getAttribute('type')) source.type = sourceEl.getAttribute('type');
      video.appendChild(source);
      return video;
    }

    function initInlineGalleryVideos() {
      var reduced =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) return;

      section.querySelectorAll('[data-lightbox-video]').forEach(function (lightbox) {
        // external_video (YouTube/Vimeo) renders an iframe, not a <video>, so it
        // is skipped here and keeps its click-to-open behaviour.
        var lightboxVideo = lightbox.querySelector('video');
        if (!lightboxVideo) return;
        var sourceEl = lightboxVideo.querySelector('source');
        if (!sourceEl || !sourceEl.getAttribute('src')) return;

        // Lightbox ids are `oddit-pglb-{sectionId}-{n}`, where n indexes
        // gallery_media — the same number the slide carries as data-slide-index.
        var lightboxId = lightbox.id;
        var mediaIndex = Number(lightboxId.split('-').pop());

        var slide = section.querySelector(
          '.oddit-section-main-product__slide[data-slide-index="' + mediaIndex + '"]'
        );
        if (slide) {
          var posterImg = slide.querySelector('.oddit-section-main-product__main-image-img');
          if (posterImg && posterImg.tagName === 'IMG') {
            var slideVideo = buildInlineVideo(
              sourceEl,
              posterImg.currentSrc || posterImg.src,
              'oddit-section-main-product__main-image-img oddit-section-main-product__main-video'
            );
            posterImg.parentNode.replaceChild(slideVideo, posterImg);
            var zoomWrap = slide.querySelector('[data-zoom-image]');
            if (zoomWrap) {
              zoomWrap.removeAttribute('data-zoom-image');
              zoomWrap.style.cursor = '';
            }
            inlineVideos.push(slideVideo);
          }
        }

        // Desktop grid tile. The <button> wrapper is left intact so a click still
        // opens the lightbox, which is where it plays large with sound.
        var tile = section.querySelector(
          '.oddit-section-main-product__grid-item--video[popovertarget="' + lightboxId + '"]'
        );
        if (tile) {
          var tileImg = tile.querySelector('.oddit-section-main-product__grid-img');
          if (tileImg && tileImg.tagName === 'IMG') {
            var tileVideo = buildInlineVideo(
              sourceEl,
              tileImg.currentSrc || tileImg.src,
              'oddit-section-main-product__grid-img'
            );
            tileImg.parentNode.replaceChild(tileVideo, tileImg);
            // The tile is moving now, so the big play badge just adds noise.
            var badge = tile.querySelector('.oddit-section-main-product__media-badge');
            if (badge) badge.style.display = 'none';
            inlineVideos.push(tileVideo);
          }
        }
      });

      if (!inlineVideos.length) return;

      function attemptPlay(video) {
        var playing = video.play();
        if (playing && playing.catch) playing.catch(function () {});
      }

      // Only play what is actually on screen: an off-screen carousel slide
      // shouldn't pull video data on mobile.
      if ('IntersectionObserver' in window) {
        var videoObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                attemptPlay(entry.target);
              } else {
                entry.target.pause();
              }
            });
          },
          { threshold: 0.25 }
        );
        inlineVideos.forEach(function (video) {
          videoObserver.observe(video);
        });
      } else {
        inlineVideos.forEach(attemptPlay);
      }

      // Don't leave the inline copy decoding behind the lightbox overlay.
      section
        .querySelectorAll('.oddit-section-main-product__lightbox')
        .forEach(function (lightbox) {
          lightbox.addEventListener('toggle', function (event) {
            if (event.newState === 'open') {
              inlineVideos.forEach(function (video) {
                video.pause();
              });
            }
          });
        });
    }

    initInlineGalleryVideos();

    var mainZoom = section.querySelector('[data-main-zoom]');
    var mainZoomImg = mainZoom
      ? mainZoom.querySelector('[data-main-zoom-img]')
      : null;
    if (mainZoom && mainZoomImg && mainZoom.showPopover) {
      section.querySelectorAll('[data-zoom-image]').forEach(function (zoomEl) {
        var zoomImg = zoomEl.querySelector('.oddit-section-main-product__main-image-img');
        if (!zoomImg || zoomImg.tagName !== 'IMG') return;
        zoomEl.style.cursor = 'zoom-in';
        zoomEl.addEventListener('click', function () {
          mainZoomImg.src = zoomImg.currentSrc || zoomImg.src;
          if (zoomImg.alt) mainZoomImg.alt = zoomImg.alt;
          mainZoom.showPopover();
        });
      });
    }

    // Now that the lightbox fills the viewport (so it blocks clicks to the page
    // behind), it no longer light-dismisses on a backdrop click. Restore that:
    // clicking the overlay itself (outside the centered media) closes it.
    // Escape still closes via the native popover behavior.
    section
      .querySelectorAll('.oddit-section-main-product__lightbox')
      .forEach(function (lightbox) {
        lightbox.addEventListener('click', function (event) {
          if (event.target === lightbox && lightbox.hidePopover) {
            lightbox.hidePopover();
          }
        });
      });

    // Lightbox videos: play when the popover opens, pause + reset when it closes.
    // (External-video iframes can't be controlled here, so they keep their own controls.)
    section.querySelectorAll('[data-lightbox-video]').forEach(function (lightbox) {
      var video = lightbox.querySelector('video');
      if (!video) return;
      lightbox.addEventListener('toggle', function (event) {
        if (event.newState === 'open') {
          var playing = video.play();
          if (playing && playing.catch) playing.catch(function () {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      });
    });

    // Single-select toggle helper. onSelect fires after the active class moves.
    function bindSingleSelect(buttons, activeClass, onSelect) {
      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (
            btn.disabled ||
            btn.classList.contains('oddit-section-main-product__color-swatch--unavailable') ||
            btn.classList.contains('oddit-section-main-product__width-option--unavailable') ||
            btn.classList.contains('oddit-section-main-product__size-option--unavailable')
          ) {
            return;
          }
          buttons.forEach(function (b) {
            b.classList.remove(activeClass);
          });
          btn.classList.add(activeClass);
          if (onSelect) onSelect();
        });
      });
    }

    /* ---- Variant resolution ------------------------------------------------
       Color is a separate product (handled by <a> swatches), so the only
       in-product variant dimensions are width/size. We resolve the selected
       option values to a variant and keep the add-to-cart buttons, price,
       availability and URL in sync. */
    var variants = [];
    var dataEl = section.querySelector('[data-oddit-product-variants]');
    if (dataEl) {
      try {
        variants = JSON.parse(dataEl.textContent) || [];
      } catch (e) {
        variants = [];
      }
    }

    var atcButtons = section.querySelectorAll('[data-add-to-cart]');
    var priceEls = section.querySelectorAll('[data-price], [data-sticky-atc-price]');
    var comparePriceEls = section.querySelectorAll('[data-compare-price]');
    var variantEls = section.querySelectorAll('[data-sticky-atc-variant]');

    // Sticky bar variant line: the variant's options (color / size / width),
    // matching the rendered variant title.
    function stickyVariantText(variant) {
      var opts = variant.options || [];
      if (opts.length === 1 && opts[0] === 'Default Title') return '';
      return opts.join(' / ');
    }

    function getSelectedOptions() {
      var map = {};
      section
        .querySelectorAll(
          '.oddit-section-main-product__width-option--active, .oddit-section-main-product__size-option--active'
        )
        .forEach(function (btn) {
          var idx = btn.getAttribute('data-variant-option-index');
          var val = btn.getAttribute('data-variant-option-value');
          if (idx !== null) map[idx] = val;
        });
      return map;
    }

    function findVariant(map) {
      var keys = Object.keys(map);
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var match = keys.every(function (idx) {
          return v.options && v.options[Number(idx)] === map[idx];
        });
        if (match) return v;
      }
      return null;
    }

    /* Three states, not two. "No size chosen yet" must never read SOLD OUT — telling a
       shopper an in-stock boot is sold out is worse than the bug that started all this.
       The needs-selection state stays clickable (aria-disabled, not disabled) so the tap
       can scroll to the size grid, and keeps no data-variant-id so nothing can be added. */
    function setButtonState(btn, available, needsSelection) {
      var label = btn.querySelector('[data-button-label]');
      var txt;
      if (needsSelection) {
        btn.disabled = false;
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('is-needs-selection');
        txt = btn.getAttribute('data-label-noselection') || btn.getAttribute('data-label-soldout');
      } else {
        btn.disabled = !available;
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('is-needs-selection');
        txt = available
          ? btn.getAttribute('data-label-available')
          : btn.getAttribute('data-label-soldout');
      }
      if (label && txt) label.textContent = txt;
    }

    /* Shop Pay's express button reads its own form's name="id" at click time. Keep it in
       step with the selection, and make the button unusable whenever there is nothing (or
       nothing in stock) to buy — otherwise it checks out whatever the server rendered,
       which was always the first variant, size 6. */
    var shopPayWraps = section.querySelectorAll('[data-shop-pay-wrap]');

    function syncShopPay(variant, needsSelection) {
      shopPayWraps.forEach(function (wrap) {
        var input = wrap.querySelector('[data-shop-pay-variant]');
        var blocked = needsSelection || !variant || !variant.available;
        // Only ever write a REAL variant id. Shopify's button reads this at click time, but
        // an empty value makes it check out some other variant — so while blocked the value
        // is left alone and the capture listener below is what stops the purchase.
        if (input && !blocked) {
          input.value = String(variant.id);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        wrap.classList.toggle('is-needs-selection', !!needsSelection);
        wrap.classList.toggle('is-unavailable', !needsSelection && blocked);
      });
    }

    // Capture phase on the wrapper runs BEFORE the event reaches Shopify's button (even inside
    // its shadow DOM), so stopping it here blocks a tap and a keyboard Enter/Space alike.
    shopPayWraps.forEach(function (wrap) {
      wrap.addEventListener(
        'click',
        function (e) {
          var needs = wrap.classList.contains('is-needs-selection');
          if (!needs && !wrap.classList.contains('is-unavailable')) return;
          e.preventDefault();
          e.stopPropagation();
          if (needs) promptForSize();
        },
        true
      );
    });

    /* Tapping add-to-cart with no size chosen has to go somewhere, or it reads as a dead
       button and the shopper leaves. Scroll the size grid into view and flash it. */
    function promptForSize() {
      var group =
        section.querySelector('.oddit-section-main-product__variant-group--size') ||
        section.querySelector('.oddit-section-main-product__size-options');
      if (!group) return;
      try {
        group.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        group.scrollIntoView();
      }
      group.classList.remove('is-size-prompt');
      void group.offsetWidth; // force reflow so the animation restarts on a repeat tap
      group.classList.add('is-size-prompt');
      setTimeout(function () {
        group.classList.remove('is-size-prompt');
      }, 1600);
    }

    function updateUrl(variantId) {
      if (!window.history || !window.history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        // The rc_size / rc_width hand-off params have done their job by now; drop them so
        // a copied or shared URL carries only ?variant=.
        url.searchParams.delete('rc_size');
        url.searchParams.delete('rc_width');
        window.history.replaceState({ variant: variantId }, '', url.toString());
      } catch (e) {
        /* no-op */
      }
    }

    /* The "WIDTH: REGULAR" / "SIZE: 6" text is server-rendered from
       option.selected_value, so without this it kept showing the first value no
       matter what was clicked. Driven off the active buttons rather than the
       resolved variant, so the label is still correct for an option combination
       that matches no variant. */
    var labelValueEls = section.querySelectorAll('[data-variant-label-value]');

    function updateOptionLabels() {
      if (!labelValueEls.length) return;
      var map = getSelectedOptions();
      labelValueEls.forEach(function (el) {
        var val = map[el.getAttribute('data-variant-label-value')];
        // No active button for this option means nothing is selected — show a dash rather
        // than leaving the server-rendered first value sitting there. "SIZE: 6" above an
        // unselected grid is the same lie that caused the accidental size-6 orders.
        el.textContent = val ? val.toUpperCase() : '—';
      });
    }

    /* ---- Carrying the size across a colour change -------------------------
       Each colour is a SEPARATE PRODUCT, so a colour swatch is an <a> and picking a
       colour is a page navigation. The destination page server-rendered its own first
       option value — size 6 — as active, with add-to-cart already armed to it. A shopper
       who chose size 10, switched colour and hit Add to Cart bought a 6, and the page
       gave no sign anything had changed. Size 6 ran 2.3x its neighbouring sizes in the 30
       days to 2026-09-23 because of this.

       Fix: the swatch links carry the current selection as rc_size / rc_width, and the
       destination reapplies it on load. Option VALUES, not a variant id — variant ids
       belong to one product and mean nothing on the next one. */
    var CARRY_SIZE = 'rc_size';
    var CARRY_WIDTH = 'rc_width';

    function syncColorSwatchLinks() {
      var swatches = section.querySelectorAll('a.oddit-section-main-product__color-swatch');
      if (!swatches.length) return;

      var sizeBtn = section.querySelector('.oddit-section-main-product__size-option--active');
      var widthBtn = section.querySelector('.oddit-section-main-product__width-option--active');
      var parts = [];
      if (sizeBtn) {
        parts.push(CARRY_SIZE + '=' + encodeURIComponent(sizeBtn.getAttribute('data-variant-option-value')));
      }
      if (widthBtn) {
        parts.push(CARRY_WIDTH + '=' + encodeURIComponent(widthBtn.getAttribute('data-variant-option-value')));
      }

      swatches.forEach(function (link) {
        // Remember the original href once, so repeated calls never stack query strings.
        var base = link.getAttribute('data-href-base');
        if (base === null) {
          base = (link.getAttribute('href') || '').split('?')[0];
          link.setAttribute('data-href-base', base);
        }
        if (!base) return;
        link.setAttribute('href', parts.length ? base + '?' + parts.join('&') : base);
      });
    }

    /* Reapply a carried selection on load. Returns true if anything was carried.
       An unavailable or non-existent size leaves NOTHING selected on purpose: the
       completeness guard in updateSelection() then disables add-to-cart until the shopper
       picks a size. Silently falling back to the first size is the bug being fixed. */
    function applyCarriedSelection() {
      var params;
      try {
        params = new URL(window.location.href).searchParams;
      } catch (e) {
        return false;
      }
      var wantSize = params.get(CARRY_SIZE);
      var wantWidth = params.get(CARRY_WIDTH);
      if (!wantSize && !wantWidth) return false;

      // Width first — refreshSizeAvailability() judges sizes against the selected width.
      if (wantWidth) {
        var widthBtns = section.querySelectorAll('.oddit-section-main-product__width-option');
        var widthMatch = null;
        widthBtns.forEach(function (btn) {
          if (btn.getAttribute('data-variant-option-value') === wantWidth) widthMatch = btn;
        });
        if (widthMatch && !widthMatch.disabled) {
          widthBtns.forEach(function (btn) {
            btn.classList.remove('oddit-section-main-product__width-option--active');
          });
          widthMatch.classList.add('oddit-section-main-product__width-option--active');
        }
      }

      refreshSizeAvailability();

      if (wantSize) {
        var sizeBtns = section.querySelectorAll('.oddit-section-main-product__size-option');
        var sizeMatch = null;
        sizeBtns.forEach(function (btn) {
          if (btn.getAttribute('data-variant-option-value') === wantSize) sizeMatch = btn;
        });
        // Clear first, then re-select only if this colour actually offers that size in
        // stock. Not offered -> no size selected -> add-to-cart stays disabled.
        sizeBtns.forEach(function (btn) {
          btn.classList.remove('oddit-section-main-product__size-option--active');
        });
        if (sizeMatch && !sizeMatch.disabled) {
          sizeMatch.classList.add('oddit-section-main-product__size-option--active');
        }
      }

      return true;
    }

    function onOptionChange() {
      updateOptionLabels();
      refreshSizeAvailability();
      updateSelection();
      syncColorSwatchLinks();
    }

    // The size buttons are server-rendered once, against whichever width was selected on
    // load. Re-run the same availability test client-side after every change so the grid
    // tracks the chosen width: a size with no in-stock variant in that width is struck
    // through and disabled, exactly as if it were sold out.
    function refreshSizeAvailability() {
      var widthBtn = section.querySelector('.oddit-section-main-product__width-option--active');
      if (!widthBtn) return; // no width option on this product — nothing to filter by
      var widthIdx = Number(widthBtn.getAttribute('data-variant-option-index'));
      var widthVal = widthBtn.getAttribute('data-variant-option-value');

      section
        .querySelectorAll('.oddit-section-main-product__size-option')
        .forEach(function (btn) {
          var sizeIdx = Number(btn.getAttribute('data-variant-option-index'));
          var sizeVal = btn.getAttribute('data-variant-option-value');
          var ok = variants.some(function (v) {
            return (
              v.available &&
              v.options &&
              v.options[sizeIdx] === sizeVal &&
              v.options[widthIdx] === widthVal
            );
          });

          btn.classList.toggle('oddit-section-main-product__size-option--unavailable', !ok);
          btn.disabled = !ok;

          // If the size that was selected is not offered in the new width, drop the
          // selection rather than leaving an impossible pair highlighted. updateSelection()
          // then finds no variant and disables add-to-cart until a valid size is picked.
          if (!ok) {
            btn.classList.remove('oddit-section-main-product__size-option--active');
          }
        });
    }

    function updateSelection() {
      if (!variants.length) return;

      // Every rendered picker must have a choice before a variant can be resolved.
      // Without this, deselecting a size (see refreshSizeAvailability) would leave a map
      // of {width: "Wide"} alone, and findVariant would happily match the first Wide
      // variant — arming add-to-cart with a size nobody picked.
      var selected = getSelectedOptions();
      var incomplete = false;
      var seenIdx = {};
      section
        .querySelectorAll('[data-variant-option-index]')
        .forEach(function (btn) {
          seenIdx[btn.getAttribute('data-variant-option-index')] = true;
        });
      Object.keys(seenIdx).forEach(function (idx) {
        if (selected[idx] === undefined) incomplete = true;
      });

      if (incomplete) {
        atcButtons.forEach(function (btn) {
          btn.removeAttribute('data-variant-id');
          setButtonState(btn, false, true);
        });
        syncShopPay(null, true);
        return;
      }

      var variant = findVariant(selected);

      // No variant for this combination (e.g. Wide + 6, which does not exist on most
      // colourways). This used to `return` silently, leaving the LAST valid variant id on
      // the add-to-cart button — so the shopper saw "WIDTH: WIDE / SIZE: 6" and carted a
      // Regular 6. Fail loudly instead: drop the id and disable the button.
      if (!variant) {
        atcButtons.forEach(function (btn) {
          btn.removeAttribute('data-variant-id');
          setButtonState(btn, false);
        });
        syncShopPay(null, false);
        return;
      }

      atcButtons.forEach(function (btn) {
        btn.setAttribute('data-variant-id', variant.id);
        setButtonState(btn, variant.available);
      });
      syncShopPay(variant, false);

      if (variant.price) {
        priceEls.forEach(function (el) {
          el.textContent = variant.price;
        });
      }

      // Markdown pricing: reveal the struck-through original only for variants that
      // carry one. Driven off the variant map, so a sale size and a full-price size
      // in the same product each show the right thing.
      comparePriceEls.forEach(function (el) {
        if (variant.compare_at_price) {
          el.textContent = variant.compare_at_price;
          el.removeAttribute('hidden');
        } else {
          el.textContent = '';
          el.setAttribute('hidden', '');
        }
      });

      variantEls.forEach(function (el) {
        el.textContent = stickyVariantText(variant);
      });

      updateUrl(variant.id);
    }

    // Color swatches are <a> links (each color is a separate product) — selection is
    // navigation, so no active-toggle binding here. Width/size are real variant toggles.
    bindSingleSelect(
      section.querySelectorAll('.oddit-section-main-product__width-option'),
      'oddit-section-main-product__width-option--active',
      onOptionChange
    );
    bindSingleSelect(
      section.querySelectorAll('.oddit-section-main-product__size-option'),
      'oddit-section-main-product__size-option--active',
      onOptionChange
    );

    /* Init. onOptionChange() only runs when something was carried in, so an ordinary
       first visit still renders exactly as the server sent it. The swatch links are
       always primed, so the very first colour click carries the current selection. */
    if (applyCarriedSelection()) {
      onOptionChange();
    } else {
      syncColorSwatchLinks();
    }

    /* ---- Add to cart → cart drawer ----------------------------------------
       The cart drawer's own click handler only covers buttons inside the drawer,
       so the product-page ATC buttons need wiring. OdditCartDrawer.addToCart adds
       the line and opens the drawer; fall back to a standard add + cart redirect. */
    function addToCartFromButton(btn) {
      if (btn.getAttribute('aria-disabled') === 'true') {
        promptForSize();
        return;
      }
      var variantId = Number(btn.getAttribute('data-variant-id'));
      if (!variantId || btn.disabled) return;

      btn.disabled = true;
      btn.classList.add('is-loading');
      function done() {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }

      if (window.OdditCartDrawer && window.OdditCartDrawer.addToCart) {
        window.OdditCartDrawer.addToCart(variantId, 1).then(done).catch(done);
      } else {
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
          .then(function () {
            window.location.href = '/cart';
          })
          .catch(done);
      }
    }

    atcButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        addToCartFromButton(btn);
      });
    });
  }

  function initAll(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll('.oddit-section-main-product');
    sections.forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Theme editor: re-init when a section is added/updated
  document.addEventListener('shopify:section:load', function (event) {
    if (event && event.target) {
      initAll(event.target);
    }
  });
})();